import {h} from 'preact';
import {useCallback, useEffect, useState} from "preact/hooks";
import {spellCheck} from "../canvas/menu-object";
import Text from "../canvas/text";
import LoadingSpinner from "./LoadingSpinner";
import constants from "../constants";
import aiService from "../service/aiService";
import store from "../store";
import {CopilotView} from "./MenuCopilot";
import designService from "../service/designService";
import Renderer from "../canvas/renderer";

interface Word {
  current: string;
  correct: string;
}

interface MenuCopilotSpellCheckProps {
  visible: boolean;
  setView: (view: CopilotView) => void;
  setCurrentCount: (view: number) => void;
  setCountLimit: (view: number) => void;
  isPaidMember: boolean;
  designId: string;
}

export default function MenuCopilotSpellCheck({
                                                visible,
                                                setView,
                                                isPaidMember,
                                                setCurrentCount,
                                                setCountLimit,
                                                designId,
                                              }: MenuCopilotSpellCheckProps) {
  const [words, setWords] = useState<Word[]>([]);
  const [wordCount, setWordCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isFinishedFlow, setFinishedFlow] = useState(false);

  const populateData = useCallback((data: string) => {
    let spelling = {} as { [key: string]: string };
    const botResponse = JSON.parse(data).response;

    try {
      spelling = JSON.parse(botResponse.replace(/\n/g, "").replace("json", "")).spell_check || {};
    } catch (err) {
      console.log(err);
    }

    window.spellCheckIgnore = window.spellCheckIgnore ?? [];
    let rry = Object
      .keys(spelling)
      .map(key => ({
        current: key,
        correct: spelling[key]
      }))
      .filter(it => !!it.correct)
      .filter(it => !window.spellCheckIgnore.find((ign: Word) => ign.current === it.current));

    const newWords = rry.sort((a, b) => a.current.localeCompare(b.current));
    if (newWords.length > 0) {
      setWords(newWords);
      setWordCount(newWords.length);
      setFinishedFlow(false);
    }
    storeWordsLocally(newWords);
  }, []);

  const performSpellCheck = useCallback(() => {
    if (!visible || !isPaidMember) return;

    setLoading(true);
    const {memberId, designId} = store.getState();

    designService.updateDesignHtml({
      id: designId,
      html: Renderer.getPagesContent(),
    }).then(() => {
      aiService.spellCheck(designId, memberId, false).then(resp => {
        setLoading(false);

        const {data, currentCount, countLimit} = resp.data;
        setCurrentCount(currentCount);
        setCountLimit(countLimit);

        populateData(data);
      });
    })
  }, [visible, isPaidMember]);

  useEffect(() => {
    const localWordsJson = window.localStorage.getItem("bot-spell-check-words-" + designId);
    if (localWordsJson && wordCount === 0) {
      let localWords = [] as Word[];
      try {
        localWords = JSON.parse(localWordsJson);
      } catch (ex) { console.error(ex); }
      setWords(localWords);
    } else {
      if (wordCount === 0 && !isFinishedFlow) {
        performSpellCheck();
      }
    }
  }, [visible, isPaidMember, wordCount]);

  function storeWordsLocally(words: Word[]) {
    const data = JSON.stringify(words);
    window.localStorage.setItem("bot-spell-check-words-" + designId, data);
  }

  function ignore(word: Word) {
    window.spellCheckIgnore = window.spellCheckIgnore ?? [];
    window.spellCheckIgnore.push(word);

    const newWords = words.filter(it => it.current !== word.current);
    setWords(newWords);
    setWordCount(newWords.length);
    storeWordsLocally(newWords);
    if (newWords.length === 0) {
      setFinishedFlow(true);
    }
  }

  function replace(word: Word) {
    spellCheck(word.current, word.correct);
    Text.spellCheckText(word.current, word.correct);

    const newWords = words.filter(it => it.current !== word.current);
    setWords(newWords);
    setWordCount(newWords.length);
    storeWordsLocally(newWords);
    if (newWords.length === 0) {
      setFinishedFlow(true);
    }
  }

  return <section className={`bot-section ${visible ? "visible" : ""}`}>
    <header>
      <button className={"back"} onClick={() => setView(CopilotView.Overview)}>
        <svg>
          <use xlinkHref={constants.appPrefix + "/svg/icons.svg#app-back-icon"}/>
        </svg>
      </button>

      <svg className={"icn"}>
        <use xlinkHref={constants.appPrefix + "/svg/icons.svg#spellcheck"}/>
      </svg>

      <h3>
        Spell Check
        <div className={"beta"}>Beta</div>
      </h3>

      <div className={"left"}>
        <button className={"refresh"} onClick={performSpellCheck}>
          <svg>
            <use xlinkHref={constants.appPrefix + "/svg/icons.svg#refresh"}/>
          </svg>
        </button>
      </div>
    </header>

    {loading && <LoadingSpinner/>}

    {!loading && <>
      {words.length > 0 &&
        <p className={"info"}>We found {Object.keys(words).length} possible misspellings:</p>
      }

      {words.length === 0 &&
        <p className={"info"}>No misspellings found.</p>
      }

      <ul>
        {words.map(word => <li>
          <h5>{word.current}</h5>
          <span> change to </span>
          <h6>{word.correct}</h6>
          <div className="actions">
            <button onClick={() => replace(word)}>replace</button>
            <button onClick={() => ignore(word)}>ignore</button>
          </div>
        </li>)}
      </ul>
    </>}
  </section>
}
